import { format } from 'date-fns';
import { toast } from 'material-react-toastify';

import { coachApi, hubApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import { coachCalendarKeys } from '@/lib/query/keys/coachCalendar';
import { isSuccess } from '@/lib/utils/response';
import { CoachAvailableTimeslotsRequest, Timeslot } from '@addmile/backend-client';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useCoachTimeslots = (
  coachId: string | undefined,
  selectedDate: Date | undefined,
  timezone: string | undefined,
) => {
  return useQuery<Timeslot[]>({
    queryKey:
      selectedDate && coachId && timezone
        ? coachCalendarKeys.timeslots(coachId, format(selectedDate, 'yyyy-MM-dd'), timezone)
        : [],
    queryFn: async () => {
      if (!selectedDate || !coachId || !timezone) {
        return [];
      }

      try {
        const token = await getAuthToken();

        const requestData: CoachAvailableTimeslotsRequest = {
          date: format(selectedDate, 'yyyy-MM-dd'),
          timezone: timezone,
        };

        const {
          data: { timeslots },
        } = await coachApi.coachAvailableTimeslots(coachId, requestData, withAuthHeader(token));

        if (!timeslots) {
          Sentry.captureException('Failed to fetch timeslots');
          toast.error('Failed to fetch timeslots');
          return [];
        }

        return timeslots.sort((a, b) => {
          const timeA = new Date(a.fromTime).getTime();
          const timeB = new Date(b.fromTime).getTime();
          return timeA - timeB;
        });
      } catch (error) {
        Sentry.captureException(`Failed to fetch timeslots: ${error.message}`);
        toast.error(`Failed to fetch timeslots: ${error.message}`);
        return [];
      }
    },
    enabled: !!selectedDate && !!coachId && !!timezone,
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useDeleteCoachTimeslot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ coachId, timeslotId }: { coachId: string; timeslotId: number }) => {
      const token = await getAuthToken();
      const response = await hubApi.deleteTimeslotHub(coachId, timeslotId, withAuthHeader(token));
      if (!isSuccess(response.status)) {
        Sentry.captureException(`Failed to delete timeslot: ${response.statusText}`);
        toast.error(response.statusText);
      }
      return { coachId, timeslotId };
    },

    onMutate: async ({ coachId, timeslotId }) => {
      const queries = queryClient.getQueryCache().findAll({
        queryKey: ['coachCalendar', 'timeslots', coachId],
      });

      queries.forEach((query) => {
        const timeslots = queryClient.getQueryData(query.queryKey) as Timeslot[];

        if (timeslots) {
          queryClient.setQueryData(
            query.queryKey,
            timeslots.filter((slot) => slot.id !== timeslotId),
          );
        }
      });
    },

    onError: (error, { coachId }) => {
      const queries = queryClient.getQueryCache().findAll({
        queryKey: ['coachCalendar', 'timeslots', coachId],
      });

      Sentry.captureException(`Failed to delete timeslot: ${error.message}`);
      toast.error('Failed to delete timeslot');
      queries.forEach((query) => {
        queryClient.invalidateQueries({ queryKey: query.queryKey });
      });
    },
  });
};
